import { API_BASE_URL } from "../../../../config/api";
import { getCompany } from "../../../../shared/data/storage/authStorage";

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const getStoredCompanyId = (): number => {
  const rawCompanyId = getCompany()?.companyId;
  const companyId = rawCompanyId ? Number.parseInt(String(rawCompanyId), 10) : NaN;

  if (!Number.isFinite(companyId) || companyId <= 0) {
    throw new APIError("Company details not found. Please sign in again.", 400);
  }

  return companyId;
};

// Type definitions (matching types.ts)
export type ColumnType = "string" | "number" | "date";
export type ColumnCategory = "dimension" | "metric";
export type DateGrouping = "month" | "quarter" | "year" | null;
export type AggregationType = "sum" | "avg" | "count" | "min" | "max";

export interface ExcelColumn {
  id: string;
  name: string;
  type: ColumnType;
  category: ColumnCategory;
  index: number;
}

export interface PivotField {
  id: string;
  columnId: string;
  name: string;
  type: ColumnType;
  dateGrouping?: DateGrouping;
  aggregation?: AggregationType;
  filterValues?: string[];
}

export interface PivotConfig {
  globalFilters: PivotField[];
  rowGroups: PivotField[];
  columnLabels: PivotField[];
  values: PivotField[];
}

export interface PivotTableData {
  rows: string[][];
  columns: string[];
  data: (number | string)[][];
}

export interface ColumnMapping {
  originalName: string;
  columnId: string;
  type: ColumnType;
  category: ColumnCategory;
}

export interface SavedPivotView {
  id: string;
  name: string;
  fileId?: string;
  tabIndex?: number;
  setName?: string; // Shared name for a group of tabs (same fileId)
  columnMappings: ColumnMapping[];
  config: PivotConfig;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  lastPeriod?: string;
  lastLead?: string;
  lastUsedFileId?: string;
  isEnabled?: boolean; // Whether the register set is enabled
  disabledAt?: string | null; // Timestamp when disabled
}

// API Response Types
interface UploadExcelResponse {
  success: boolean;
  data: {
    fileId: string;
    headers: string[];
    columns: ExcelColumn[];
    rowCount: number;
    sampleRows?: any[][];
  };
}

interface GeneratePivotResponse {
  success: boolean;
  data: PivotTableData & {
    metadata: {
      totalRows: number;
      totalColumns: number;
      generatedAt: string;
    };
  };
}

interface PivotViewResponse {
  success: boolean;
  data: SavedPivotView;
}

interface PivotViewsResponse {
  success: boolean;
  data: SavedPivotView[];
}

interface UpdateSetNameResponse {
  success: boolean;
  data: {
    fileId: string;
    setName: string;
    views: SavedPivotView[];
  };
}

/**
 * Upload Excel file and parse columns
 * @param file Excel file (.xlsx or .xls)
 * @param period Optional period (e.g., "jan-25")
 * @param lead Optional lead (e.g., "revenue")
 * @param leadLevelId Optional lead level ID (from cfo_mcoa_levels.id)
 * @returns Parsed Excel data with columns
 */
export const uploadExcelFile = async (
  file: File,
  period?: string,
  lead?: string,
  leadLevelId?: number | null
): Promise<UploadExcelResponse["data"]> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_id", String(getStoredCompanyId()));
  if (period) formData.append("period", period);
  if (lead) formData.append("lead", lead);
  if (leadLevelId) formData.append("lead_level_id", leadLevelId.toString());

  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/upload-excel`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to upload Excel file",
        response.status,
        errorData
      );
    }

    const result: UploadExcelResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Get uploaded file metadata by fileId
 * @param fileId File ID
 * @param companyId Optional company ID
 * @returns File metadata including headers, columns, rowCount
 */
export const getUploadedFile = async (
  fileId: string,
  companyId: number = getStoredCompanyId()
): Promise<{
  fileId: string;
  headers: string[];
  columns: ExcelColumn[];
  rowCount: number;
  period?: string;
  lead?: string;
}> => {
  try {
    const params = `?company_id=${companyId}`;
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/file/${fileId}${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to get file",
        response.status,
        errorData
      );
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Generate pivot table from configuration
 * @param fileId File ID from upload
 * @param config Pivot configuration
 * @param period Period (e.g., "jan-25")
 * @param lead Lead (e.g., "revenue")
 * @returns Generated pivot table data
 */
export const generatePivotTable = async (
  fileId: string,
  config: PivotConfig,
  period: string,
  lead: string
): Promise<PivotTableData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileId,
        config,
        period,
        lead,
        companyId: getStoredCompanyId(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to generate pivot table",
        response.status,
        errorData
      );
    }

    const result: GeneratePivotResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Get all saved pivot views
 * @param companyId Optional company ID (defaults to user's company)
 * @returns Array of saved pivot views
 */
export const getPivotViews = async (
  companyId: number = getStoredCompanyId(),
  fileId?: string,
  status?: 'all' | 'active' | 'disabled'
): Promise<SavedPivotView[]> => {
  try {
    const params = new URLSearchParams();
    params.append('company_id', companyId.toString());
    if (fileId) params.append('file_id', fileId);
    if (status) params.append('status', status);

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/cfo/pivot/views${queryString ? `?${queryString}` : ""}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to load pivot views",
        response.status,
        errorData
      );
    }

    const result: PivotViewsResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Get single pivot view by ID
 * @param viewId Pivot view ID
 * @returns Saved pivot view
 */
export const getPivotView = async (
  viewId: string,
  companyId: number = getStoredCompanyId()
): Promise<SavedPivotView> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/pivot/views/${viewId}?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new APIError("Pivot view not found", 404);
      }
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to load pivot view",
        response.status,
        errorData
      );
    }

    const result: PivotViewResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Save new pivot view
 * @param view Pivot view to save (without id, createdAt, updatedAt)
 * @param userId Optional user ID (defaults to 1 if not provided)
 * @param companyId Optional company ID (defaults to stored company)
 * @returns Saved pivot view with ID
 */
export const savePivotView = async (
  view: Omit<SavedPivotView, "id" | "createdAt" | "updatedAt">,
  userId: number = 1,
  companyId: number = getStoredCompanyId()
): Promise<SavedPivotView> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/views`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...view,
        userId,
        companyId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to save pivot view",
        response.status,
        errorData
      );
    }

    const result: PivotViewResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Update existing pivot view
 * @param viewId Pivot view ID
 * @param view Updated pivot view data
 * @param userId User ID
 * @param companyId Company ID
 * @returns Updated pivot view
 */
export const updatePivotView = async (
  viewId: string,
  view: Partial<SavedPivotView>,
  userId: number,
  companyId: number = getStoredCompanyId()
): Promise<SavedPivotView> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/pivot/views/${viewId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...view,
          userId,
          companyId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to update pivot view",
        response.status,
        errorData
      );
    }

    const result: PivotViewResponse = await response.json();
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Delete pivot view
 * @param viewId Pivot view ID
 */
export const deletePivotView = async (
  viewId: string,
  companyId: number = getStoredCompanyId()
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/pivot/views/${viewId}?company_id=${companyId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to delete pivot view",
        response.status,
        errorData
      );
    }
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Update last used timestamp for pivot view
 * @param viewId Pivot view ID
 * @param period Optional period
 * @param lead Optional lead
 * @param fileId Optional file ID (last used file)
 */
export const updatePivotViewLastUsed = async (
  viewId: string,
  period?: string,
  lead?: string,
  fileId?: string,
  companyId: number = getStoredCompanyId()
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/pivot/views/${viewId}/last-used`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ period, lead, fileId, companyId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to update last used timestamp",
        response.status,
        errorData
      );
    }
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Get file history for a pivot view
 * GET /api/cfo/pivot/views/{viewId}/file-history
 */
export interface FileHistoryEntry {
  id: string;
  fileId: string;
  fileName: string;
  period?: string;
  lead?: string;
  usedAt: string;
  fileSize: number;
  rowCount: number;
}

export const getFileHistory = async (
  viewId: string,
  companyId: number = getStoredCompanyId()
): Promise<FileHistoryEntry[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/cfo/pivot/views/${viewId}/file-history?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || "Failed to load file history",
        response.status,
        errorData
      );
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Export pivot table to Excel (server-side)
 * POST /api/cfo/pivot/export
 * 
 * Either provide viewId (for saved views) OR fileId + config (for unsaved views)
 */
export interface ExportPivotRequest {
  viewId?: string; // If provided, backend loads saved view
  fileId?: string; // Required if viewId not provided
  config?: {
    rowGroups: Array<{ name: string; id: string; columnId: string }>;
    columnLabels: Array<{ name: string; id: string; columnId: string }>;
    values: Array<{ name: string; aggregation?: string; id: string; columnId: string }>;
    globalFilters: Array<{ name: string; id: string; columnId?: string }>;
  }; // Required if viewId not provided
  viewName?: string;
  options?: {
    includeConfigSheet?: boolean;
    fileName?: string;
  };
  companyId?: number;
  userId?: number;
}

export const exportPivotToExcel = async (
  request: ExportPivotRequest
): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...request,
        companyId: request.companyId ?? getStoredCompanyId(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || 'Failed to export Excel file',
        response.status,
        errorData
      );
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'pivot_export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Get blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      'Failed to export Excel file: ' + (error instanceof Error ? error.message : 'Unknown error'),
      500
    );
  }
};

/**
 * Export all pivot tabs to Excel (multiple sheets)
 * POST /api/cfo/pivot/export-all
 */
export interface ExportAllTabsRequest {
  fileId: string;
  viewIds?: string[]; // Optional: specific view IDs to export
  viewName?: string;
  options?: {
    includeConfigSheet?: boolean;
    fileName?: string;
  };
  companyId?: number;
  userId?: number;
}

export const exportAllPivotTabsToExcel = async (
  request: ExportAllTabsRequest
): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/export-all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...request,
        companyId: request.companyId ?? getStoredCompanyId(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || 'Failed to export all tabs',
        response.status,
        errorData
      );
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'pivot_all_views.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Get blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      'Failed to export all tabs: ' + (error instanceof Error ? error.message : 'Unknown error'),
      500
    );
  }
};

/**
 * Rename a pivot view set (all tabs from the same original fileId)
 * PATCH /api/cfo/pivot/files/{fileId}/set-name
 */
export const updatePivotSetName = async (
  fileId: string,
  setName: string,
  companyId: number = getStoredCompanyId()
): Promise<UpdateSetNameResponse["data"]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cfo/pivot/files/${fileId}/set-name`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        setName,
        companyId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || "Failed to update set name",
        response.status,
        data
      );
    }

    const result: UpdateSetNameResponse = data;
    return result.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

/**
 * Toggle register set status (enable/disable all views with same fileId)
 * @param fileId The file ID that groups the register set
 * @param enabled Whether to enable (true) or disable (false) the register set
 * @param companyId Optional company ID
 * @returns Updated views data
 */
export const toggleRegisterSetStatus = async (
  fileId: string,
  enabled: boolean,
  companyId: number = getStoredCompanyId()
): Promise<{ fileId: string; enabled: boolean; viewsUpdated: number; views: SavedPivotView[] }> => {
  try {
    const params = new URLSearchParams();
    params.append('company_id', companyId.toString());

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/cfo/pivot/registers/${fileId}/toggle${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        enabled,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.message || "Failed to toggle register set status",
        response.status,
        data
      );
    }

    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};

// Register File Types (for drill-down from databook)
export interface RegisterExcelCell {
  col: string;
  row: number;
  value: string | number | null;
  formula: string | null;
}

export interface RegisterExcelSheet {
  rows: RegisterExcelCell[][];
  maxCol: string;
  maxRow: number;
}

export interface RegisterFileData {
  fileId: string;
  fileName: string;
  lead: string;
  period: string;
  sheetNames: string[];
  sheets: { [sheetName: string]: RegisterExcelSheet };
}

/**
 * Get register file content by lead path and period
 * Used for drill-down from databook Source Sheet to view the actual register Excel file
 * @param lead Lead path (e.g., "IS > Employee cost > Salaries")
 * @param period Period (e.g., "jan-25")
 * @param companyId Company ID
 * @param leadLevelId Optional lead level ID (more reliable than string matching)
 * @returns Parsed register Excel data with sheets
 */
export const getRegisterFile = async (
  lead: string,
  period: string,
  companyId: number = getStoredCompanyId(),
  leadLevelId?: number
): Promise<RegisterFileData | null> => {
  try {
    const params = new URLSearchParams();
    params.append('lead', lead);
    if (period) params.append('period', period);
    params.append('company_id', companyId.toString());
    if (leadLevelId) params.append('lead_level_id', leadLevelId.toString());

    const url = `${API_BASE_URL}/api/cfo/pivot/register-file?${params.toString()}`;    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      // Return null for 404 (register not found) instead of throwing
      if (response.status === 404) {
        return null;
      }
      throw new APIError(
        data.message || "Failed to get register file",
        response.status,
        data
      );
    }

    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred",
      0,
      error
    );
  }
};